import React from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Plus, Eye, Edit, Trash2, Star } from "lucide-react";
import type { AdminPlan } from "@/services/adminService";
import { Switch } from "@/components/ui/switch";
import { useDispatch } from "react-redux";
// import type { AppDispatch } from "@/store"; // Décommentez si AppDispatch existe
import { updatePlan } from "@/store/slices/adminSlice";
import { toast } from "sonner";

interface PlansTabProps {
  plans: AdminPlan[];
  plansLoading: boolean;
  plansError: string | null;
  onCreatePlan: () => void;
  onViewPlan: (plan: AdminPlan) => void;
  onEditPlan: (plan: AdminPlan) => void;
  onDeletePlan: (plan: AdminPlan) => void;
}

export const PlansTab: React.FC<PlansTabProps> = ({
  plans,
  plansLoading,
  plansError,
  onCreatePlan,
  onViewPlan,
  onEditPlan,
  onDeletePlan,
}) => {
  // Si AppDispatch existe dans votre projet, utilisez-le pour typer le dispatch :
  // const dispatch = useDispatch<AppDispatch>();
  const dispatch = useDispatch<any>();
  const [loadingPlanId, setLoadingPlanId] = React.useState<string | null>(null);

  const handleToggleActive = async (plan: AdminPlan) => {
    setLoadingPlanId(plan.id);
    try {
      await dispatch(
        updatePlan({ id: plan.id, data: { isActive: !plan.isActive } }),
      ).unwrap();
      toast.success(
        `Plan ${!plan.isActive ? "activé" : "désactivé"} avec succès`,
      );
    } catch (err: unknown) {
      const message =
        err instanceof Error
          ? err.message
          : "Erreur lors de la mise à jour du statut du plan";
      toast.error(message);
    } finally {
      setLoadingPlanId(null);
    }
  };

  if (plansLoading) {
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h2 className="text-2xl font-semibold">Gestion des Plans</h2>
          <Button disabled>
            <Plus className="w-4 h-4 mr-2" />
            Nouveau Plan
          </Button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {Array.from({ length: 3 }).map((_, i) => (
            <Card key={i} className="animate-pulse">
              <CardHeader>
                <div className="h-6 bg-gray-200 rounded w-1/2"></div>
                <div className="h-4 bg-gray-200 rounded w-3/4"></div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="h-8 bg-gray-200 rounded w-1/3"></div>
                  <div className="h-4 bg-gray-200 rounded"></div>
                  <div className="h-4 bg-gray-200 rounded w-5/6"></div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  if (plansError) {
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h2 className="text-2xl font-semibold">Gestion des Plans</h2>
          <Button onClick={onCreatePlan}>
            <Plus className="w-4 h-4 mr-2" />
            Nouveau Plan
          </Button>
        </div>
        <Card className="border-red-200 bg-red-50 dark:bg-red-900/20">
          <CardContent className="pt-6">
            <div className="text-center">
              <div className="text-red-600 dark:text-red-400 text-lg font-medium mb-2">
                Erreur de chargement
              </div>
              <p className="text-red-500 dark:text-red-300">{plansError}</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-semibold">Gestion des Plans</h2>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Gérez les plans d'abonnement de votre plateforme
          </p>
        </div>
        <Button onClick={onCreatePlan}>
          <Plus className="w-4 h-4 mr-2" />
          Nouveau Plan
        </Button>
      </div>

      {plans.length === 0 ? (
        <Card>
          <CardContent className="pt-6">
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-4">
                <Plus className="w-8 h-8 text-gray-400" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                Aucun plan trouvé
              </h3>
              <p className="text-gray-500 dark:text-gray-400 mb-4">
                Commencez par créer votre premier plan d'abonnement
              </p>
              <Button onClick={onCreatePlan}>
                <Plus className="w-4 h-4 mr-2" />
                Créer un plan
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[...plans]
            .sort((a, b) => a.displayOrder - b.displayOrder)
            .map((plan) => (
              <Card
                key={plan.id}
                className={`relative transition-all duration-200 hover:shadow-lg ${
                  plan.isPopular
                    ? "ring-2 ring-violet-500 ring-opacity-50 shadow-lg"
                    : ""
                }`}
              >
                {/* Popular badge */}
                {plan.isPopular && plan.badge && (
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 z-10">
                    <Badge className="bg-gradient-to-r from-violet-500 to-purple-500 text-white shadow-lg px-3 py-1">
                      <Star className="w-3 h-3 mr-1" />
                      {plan.badge}
                    </Badge>
                  </div>
                )}

                <CardHeader className="pb-4">
                  <div className="flex justify-between items-start">
                    <div>
                      <CardTitle className="text-xl font-bold text-gray-900 dark:text-white">
                        {plan.name}
                      </CardTitle>
                      <p className="text-gray-600 dark:text-gray-300 text-sm mt-1">
                        {plan.description}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge
                        variant={plan.isActive ? "default" : "secondary"}
                        className={plan.isActive ? "bg-green-500" : ""}
                      >
                        {plan.isActive ? "Actif" : "Inactif"}
                      </Badge>
                      <Switch
                        checked={plan.isActive}
                        onCheckedChange={() => handleToggleActive(plan)}
                        disabled={loadingPlanId === plan.id}
                        aria-label={
                          plan.isActive
                            ? "Désactiver le plan"
                            : "Activer le plan"
                        }
                        className="ml-2"
                      />
                    </div>
                  </div>
                </CardHeader>

                <CardContent>
                  <div className="space-y-4">
                    {/* Price */}
                    <div>
                      {plan.discountPercentage &&
                      plan.discountPercentage > 0 ? (
                        <>
                          {/* Prix original barré */}
                          <div className="text-sm text-gray-400 dark:text-gray-500 line-through mb-1">
                            {plan.currency}
                            {(
                              Number(plan.price) /
                              (1 - Number(plan.discountPercentage) / 100)
                            ).toFixed(2)}{" "}
                            HT
                          </div>
                          {/* Prix avec réduction (déjà appliquée) */}
                          <div>
                            <span className="text-3xl font-bold bg-gradient-to-r from-violet-600 to-purple-600 dark:from-violet-400 dark:to-purple-400 bg-clip-text text-transparent">
                              {plan.currency}
                              {Number(plan.price).toFixed(2)}
                            </span>
                            <span className="text-gray-500 dark:text-gray-400 ml-2">
                              HT /
                              {plan.billingInterval === "year" ? "an" : "mois"}
                            </span>
                            <span className="text-green-600 dark:text-green-400 font-semibold ml-2 text-sm">
                              (-{plan.discountPercentage}%)
                            </span>
                          </div>
                          {plan.billingInterval === "year" && (
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                              ({(Number(plan.price) / 12).toFixed(2)}{" "}
                              {plan.currency} HT/mois)
                            </p>
                          )}
                        </>
                      ) : (
                        <>
                          <span className="text-3xl font-bold bg-gradient-to-r from-violet-600 to-purple-600 dark:from-violet-400 dark:to-purple-400 bg-clip-text text-transparent">
                            {plan.currency}
                            {Number(plan.price).toFixed(2)}
                          </span>
                          <span className="text-gray-500 dark:text-gray-400 ml-2">
                            HT /
                            {plan.billingInterval === "year" ? "an" : "mois"}
                          </span>
                          {plan.billingInterval === "year" && (
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                              ({(Number(plan.price) / 12).toFixed(2)}{" "}
                              {plan.currency} HT/mois)
                            </p>
                          )}
                        </>
                      )}
                    </div>

                    {/* Features */}
                    <div className="space-y-2">
                      <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        Fonctionnalités:
                      </h4>
                      <ul className="space-y-1">
                        {plan.features.slice(0, 3).map((feature, index) => (
                          <li
                            key={index}
                            className="text-sm text-gray-600 dark:text-gray-400 flex items-center"
                          >
                            <span className="w-1.5 h-1.5 bg-green-500 rounded-full mr-2"></span>
                            {feature}
                          </li>
                        ))}
                        {plan.features.length > 3 && (
                          <li className="text-sm text-gray-500 dark:text-gray-400">
                            +{plan.features.length - 3} autres...
                          </li>
                        )}
                      </ul>
                    </div>

                    {/* Display order */}
                    <div className="text-sm text-gray-600 dark:text-gray-400">
                      <span className="font-medium">Ordre: </span>
                      {plan.displayOrder}
                    </div>

                    {/* Actions */}
                    <div className="flex gap-2 pt-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onViewPlan(plan)}
                        className="flex-1"
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        Voir
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onEditPlan(plan)}
                        className="flex-1"
                      >
                        <Edit className="w-4 h-4 mr-1" />
                        Modifier
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onDeletePlan(plan)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
        </div>
      )}
    </div>
  );
};
